/** Shared formatting + vocabulary for the stores screens. */

export const todayIso = () => new Date().toISOString().slice(0, 10);

/** First and last day of the month a date falls in, as ISO dates. */
export function monthRange(d: Date = new Date()) {
  const iso = (x: Date) =>
    `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
  return {
    from: iso(new Date(d.getFullYear(), d.getMonth(), 1)),
    to: iso(new Date(d.getFullYear(), d.getMonth() + 1, 0)),
  };
}

export function fmtDate(iso?: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? String(iso)
    : d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

/** Quantities carry two decimals in the API but read better trimmed. */
export function fmtQty(v?: number | string | null): string {
  const n = Number(v ?? 0);
  if (!isFinite(n)) return "—";
  return Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/0$/, "");
}

export function fmtNaira(v?: number | string | null): string {
  const n = Number(v ?? 0);
  if (!isFinite(n)) return "—";
  return `₦${n.toLocaleString("en-NG", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export const MOVEMENT_TYPES = [
  { value: "in", label: "Receipt (stock in)" },
  { value: "out", label: "Issue (stock out)" },
  { value: "adjustment", label: "Adjustment" },
] as const;

export function movementLabel(type?: string): string {
  switch (String(type ?? "").toLowerCase()) {
    case "in": return "Receipt";
    case "out": return "Issue";
    case "adjustment": return "Adjustment";
    case "transfer_in": return "Transferred in";
    case "transfer_out": return "Transferred out";
    case "issue": return "To staff";
    case "sale": return "Sale";
    case "return": return "Returned";
    default: return type ?? "—";
  }
}

export function movementColor(type?: string): string {
  switch (String(type ?? "").toLowerCase()) {
    case "in":           return "bg-green-100 text-green-800 border-green-200";
    case "out":          return "bg-amber-100 text-amber-800 border-amber-200";
    case "adjustment":   return "bg-violet-100 text-violet-800 border-violet-200";
    case "transfer_in":  return "bg-sky-100 text-sky-800 border-sky-200";
    case "transfer_out": return "bg-sky-100 text-sky-800 border-sky-200";
    case "issue":        return "bg-blue-100 text-blue-800 border-blue-200";
    case "sale":         return "bg-emerald-100 text-emerald-800 border-emerald-200";
    case "return":       return "bg-amber-100 text-amber-800 border-amber-200";
    default:             return "bg-gray-100 text-gray-600 border-gray-200";
  }
}

/**
 * Movements are stored unsigned except adjustments, so the sign is worked out
 * from the type — and, where a move has two sides, from WHICH side this row is.
 * An issue takes stock off the store and puts it on the person; one row, one
 * balance, opposite signs.
 */
export function signedQty(row: any): string {
  const q = Number(row?.quantity ?? 0);
  const type = String(row?.type ?? "");
  const minus = `−${fmtQty(Math.abs(q))}`;
  const plus = `+${fmtQty(Math.abs(q))}`;

  if (type === "adjustment") return `${q < 0 ? "−" : "+"}${fmtQty(Math.abs(q))}`;
  if (["out", "transfer_out", "sale"].includes(type)) return minus;
  if (type === "issue") return row?.holder_type === "store" ? minus : plus;
  if (type === "return") return row?.holder_type === "admin" ? minus : plus;
  return `+${fmtQty(q)}`;
}

/** Staff records are `admins`; the display name falls back the same way the API does. */
export function staffName(admin: any): string {
  if (!admin) return "";
  const full = [admin.firstname, admin.lastname].filter(Boolean).join(" ").trim();
  return admin.user?.name || full || admin.staff_no || "";
}

/** Who an issue actually went to — a staff member, or a written-in name. */
export function recipient(row: any): string {
  return (
    // On a row that moved a PERSON's holding — an issue, a sale, a return —
    // the person is the holder, not the "issued to" field.
    staffName(row?.holder_admin ?? row?.holderAdmin) ||
    staffName(row?.issued_to_admin ?? row?.issuedToAdmin) ||
    row?.issued_to ||
    row?.sold_to ||
    "—"
  );
}

export function errMsg(err: any): string {
  return err?.data?.message || err?.data?.error || err?.error || err?.message || "Something went wrong";
}
